import React, { FC, useContext, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  makeStyles,
  Menu,
  Box,
  Button,
  Slide,
  Dialog
} from '@material-ui/core';

import { ReactComponent as ProfileDefault } from 'src/images/profile.svg';
import { RootState, useSelector } from 'src/store';
import { AuthContext } from 'src/contexts/authContext';
import SettingsModal from 'src/components/Modal/SettingsModal';
import { TransitionProps } from '@material-ui/core/transitions';
import ChevronUp from 'src/components/Icon/ChevronUp';
import ChevronDown from 'src/components/Icon/ChevronDown';

const useStyles = makeStyles(() => ({
  profileButton: {
    padding: 16,
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    fontFamily: "'Poppins', sans-serif",
    fontWeight: 500,
    fontSize: 15,
    textTransform: 'none',
    background: '#ffffff'
  },
  profileBox: {
    height: 38,
    display: 'flex',
    alignItems: 'center'
  },
  profileInfo: {
    width: 135,
    display: 'flex',
    flexDirection: 'column',
    lineHeight: '120%',
    textAlign: 'left',
    fontSize: 14,
    fontWeight: 400,
    fontFamily: "'Poppins', sans-serif"
  },
  profileName: {
    height: 16,
    marginBottom: 4,
    fontWeight: 500,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    color: '#191F28'
  },
  profileCompany: {
    height: 15,
    fontSize: 12,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    color: '#8B95A1'
  },
  menuModal: {
    width: 260,
    marginTop: 65,
    padding: 20,
    color: '#000000',
    borderRadius: 16,
    border: '2px solid #BDBDBD',
    boxShadow: 'none',
    position: 'relative',
    fontFamily: "'Poppins', sans-serif"
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: 600,
    color: '#191F28',
    fontFamily: "'Poppins', sans-serif",
    marginBottom: 16
  },
  infoBox: {
    backgroundColor: '#F9FAFB',
    borderRadius: 12,
    padding: 16,
    marginBottom: 20
  },
  infoLabel: {
    fontSize: 12,
    color: '#8B95A1',
    fontFamily: "'Poppins', sans-serif",
    marginBottom: 4
  },
  infoValue: {
    fontSize: 14,
    fontWeight: 600,
    color: '#191F28',
    fontFamily: "'Poppins', sans-serif"
  },
  menu: {
    width: '100%',
    height: 44,
    color: '#191F28',
    cursor: 'pointer',
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    textDecoration: 'none',
    padding: '0 12px',
    borderRadius: 8,
    fontFamily: "'Poppins', sans-serif",
    fontSize: 14,
    fontWeight: 500,
    textTransform: 'none',
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: '#F9FAFB'
    }
  },
  menuDivider: {
    height: 1,
    backgroundColor: '#E6E6E6',
    margin: '8px 0'
  },
  contactFooter: {
    marginTop: 16,
    paddingTop: 12,
    borderTop: '1px solid #E6E6E6',
    fontSize: 11,
    color: '#8B95A1',
    fontFamily: "'Poppins', sans-serif",
    textAlign: 'center',
    '& a': {
      color: '#191F28',
      fontWeight: 500,
      textDecoration: 'none',
      '&:hover': {
        textDecoration: 'underline'
      }
    }
  },
  icon: {
    display: 'flex',
    alignItems: 'center'
  },
  dialog: {
    top: 0,
    right: 0,
    position: 'fixed',
    width: '50%',
    margin: 0,
    height: '100%',
    maxHeight: 'max-content',
    borderRadius: 0
  }
}));

const Account: FC<{ expireDate: any; exportedCount: number }> = ({
  expireDate,
  exportedCount
}) => {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const userCompany = useSelector(
    (state: RootState) => state.client.userCompany
  );
  const userName = useSelector((state: RootState) => state.client.userName);
  const userEmail = useSelector((state: RootState) => state.client.userEmail);
  const userGrade = useSelector((state: RootState) => state.client.userGrade);
  const USER_COMPANY = userCompany
    .split(' ')
    .map(str => str.replace(/^[a-z]/, char => char.toUpperCase()))
    .join(' ');
  const USER_NAME =
    userName === ''
      ? userEmail.split('@')[0]
      : userName.replace(/^[a-z]/, char => char.toUpperCase());

  const handleClickModal = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseModal = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <Button className={classes.profileButton} onClick={handleClickModal}>
        <Box className={classes.profileBox}>
          <ProfileDefault
            style={{
              width: '37px',
              height: '37px',
              margin: '0px 16px'
            }}
          />
          <Box className={classes.profileInfo}>
            <p className={classes.profileName}>{USER_NAME || 'username'}</p>
            <p className={classes.profileCompany}>
              {USER_COMPANY || 'company'}
            </p>
          </Box>
          {anchorEl ? <ChevronUp /> : <ChevronDown />}
        </Box>
      </Button>
      <AccountModal
        userInfo={{ userGrade, expireDate, exportedCount }}
        visible={{ anchorEl, handleCloseModal }}
      />
    </>
  );
};

const AccountModal = ({ userInfo, visible }: any) => {
  const classes = useStyles();
  const history = useHistory();
  const authContext = useContext(AuthContext);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [currentTab, setCurrentTab] = useState('');
  const [hidden, setHidden] = useState('hidden');

  async function handleLogout() {
    try {
      await authContext.signOut();
      window.location.href = '/login';
    } catch (error) {}
  }

  const handleCustomFont = () => {
    setCurrentTab('customfont');
    setSettingsOpen(true);
    setHidden('visible');
  };
  const handleBilling = () => {
    history.push('/billing/subscription');
    setHidden('visible');
  };
  const Transition = React.forwardRef(
    (
      props: TransitionProps & { children?: React.ReactElement<any, any> },
      ref: React.Ref<unknown>
    ) => <Slide direction="left" ref={ref} {...props} />
  );

  const handleModalClose = () => {
    setSettingsOpen(false);
    setHidden('hidden');
  };

  return (
    <>
      <Menu
        id="simple-menu"
        anchorEl={visible.anchorEl}
        keepMounted
        open={Boolean(visible.anchorEl)}
        onClose={visible.handleCloseModal}
        classes={{ paper: classes.menuModal }}
      >
        <div className={classes.modalTitle}>Account Info</div>
        <Box className={classes.infoBox}>
          <div style={{ marginBottom: 12 }}>
            <p className={classes.infoLabel}>Plan</p>
            <p className={classes.infoValue}>
              {userInfo.userGrade.toUpperCase()}
            </p>
          </div>
          {userInfo.expireDate === 'expired' ? (
            <div>
              <p className={classes.infoLabel}>Remain Export</p>
              <p className={classes.infoValue}>{50 - userInfo.exportedCount}</p>
            </div>
          ) : (
            <div>
              <p className={classes.infoLabel}>Expire Date</p>
              <p className={classes.infoValue}>{userInfo.expireDate}</p>
            </div>
          )}
        </Box>
        <Button onClick={handleCustomFont} className={classes.menu}>
          Custom Font
        </Button>
        <div className={classes.menuDivider} />
        <Button onClick={handleBilling} className={classes.menu}>
          Billing
        </Button>
        <div className={classes.menuDivider} />
        <Button className={classes.menu} onClick={handleLogout}>
          Logout
        </Button>
        <div className={classes.contactFooter}>
          Contact:{' '}
          <a href="mailto:iam@metric-studio.com">iam@metric-studio.com</a>
        </div>
      </Menu>
      <Dialog
        classes={{ paper: classes.dialog }}
        TransitionComponent={Transition}
        open={settingsOpen}
        onBackdropClick={handleModalClose}
        maxWidth={false}
        style={{ visibility: hidden === 'hidden' ? 'hidden' : 'visible' }}
      >
        <SettingsModal handleClose={handleModalClose} currentTab={currentTab} />
      </Dialog>
    </>
  );
};

export default Account;
